<template>
  <div class="tw-flex tw-flex-col tw-h-full tw-overflow-hidden tw-relative">
    <ModuleProgressBar />
    <div v-if="showBadge" class="tw-px-6 tw-pt-6 tw-pb-2 tw-flex tw-justify-start">
      <div
        class="tw-inline-flex tw-items-center tw-justify-start tw-gap-2 tw-px-4 tw-py-2 tw-border-2 tw-border-progress tw-text-progress tw-rounded-xl tw-font-semibold tw-text-lg tw-min-w-[140px]"
      >
        <svg
          v-if="badgeMode === 'read'"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="tw-w-5 tw-h-5"
        >
          <path d="M2 6a2 2 0 012-2h6v16H4a2 2 0 01-2-2V6z" />
          <path d="M22 6a2 2 0 00-2-2h-6v16h6a2 2 0 002-2V6z" />
        </svg>
        <svg
          v-else-if="badgeMode === 'listen'"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
          class="tw-w-5 tw-h-5"
        >
          <path d="M3 10v4a1 1 0 001 1h3l4 4V5L7 9H4a1 1 0 00-1 1z" />
          <path d="M16.5 12a4.5 4.5 0 00-2.5-4.03v8.05A4.5 4.5 0 0016.5 12z" />
          <path d="M14 3.23v2.06a7 7 0 010 13.42v2.06a9 9 0 000-17.54z" />
        </svg>
        <svg
          v-else
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="tw-w-5 tw-h-5"
        >
          <circle cx="9" cy="8" r="3.5" />
          <path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" />
          <path d="M16 7c1 1 1 3 0 4" />
          <path d="M18.5 5c2 2 2 6 0 8" />
        </svg>
        {{ badgeLabel }}
      </div>
    </div>
    <main
      v-if="isPreQuestionIntro || !isFullBleedImage || isIntroSlide || isModuleIntroStep"
      class="tw-flex-shrink-0 tw-p-6 tw-text-base tw-text-left markdown-content"
      v-html="html"
    />
    <div
      v-if="view.image && !isFullBleedImage && !isPreQuestionIntro"
      class="tw-flex-1 tw-bg-center tw-bg-cover tw-bg-no-repeat"
      :style="{ backgroundImage: `url(${originUrl}${view.image})` }"
    />
  </div>
</template>

<script>
import { marked } from 'marked';
import ModuleProgressBar from './ModuleProgressBar.vue';

export default {
  name: 'ExamQuestion',
  components: {
    ModuleProgressBar,
  },
  data() {
    return {
      readPhaseActive: false,
      readPhaseTimer: null,
    };
  },
  computed: {
    html() {
      if (this.isPreQuestionIntro) {
        return '<div class="tw-text-center tw-text-white tw-text-xl tw-font-semibold">The examiner is about to introduce the next part of the exam.</div>';
      }
      return `<div>${marked(this.$props?.view?.content)}</div>`;
    },
    originUrl() {
      return this.$store.state.originUrl;
    },
    currentModuleKey() {
      return this.$store.state.exam.modules[this.$store.state.exam.current.module]?.key;
    },
    isAudioPlaying() {
      return this.$store.state.audio.currentSource !== null;
    },
    isIntroSlide() {
      return this.$store.state.exam.current.question === null;
    },
    isModuleIntroStep() {
      return this.$props?.view?.isModuleIntroStep;
    },
    isPreQuestionIntro() {
      return this.$props?.view?.isPreQuestionIntro;
    },
    isIntroLike() {
      return this.isIntroSlide || this.isModuleIntroStep || this.isPreQuestionIntro;
    },
    hasReadPhase() {
      return this.currentModuleKey === '2A';
    },
    badgeMode() {
      if (this.hasReadPhase && this.readPhaseActive) return 'read';
      return this.isAudioPlaying ? 'listen' : 'answer';
    },
    badgeLabel() {
      if (this.badgeMode === 'read') return 'Read the Question...';
      if (this.badgeMode === 'listen') {
        if (this.currentModuleKey === '2C') return 'Listen and take notes';
        return 'Listen';
      }
      if (this.currentModuleKey === '2B') return 'Read back';
      if (this.currentModuleKey === '2C') return 'Report the message';
      if (this.currentModuleKey === '2D') return 'Ask the question';
      if (this.currentModuleKey === '1IC' && this.$store.state.exam.current.question === 0) return 'Listen';
      return 'Answer';
    },
    showBadge() {
      if (this.isModuleIntroStep) return false;
      if (this.isPreQuestionIntro) return false;
      if (this.isIntroSlide) return false;
      if (this.isFullBleedImage) return false;
      return true;
    },
    isFullBleedImage() {
      return this.currentModuleKey === '3A' || this.currentModuleKey === '3B';
    },
  },
  props: {
    view: Object,
  },
  methods: {
    handleRenderExaminerCamera(isViewVideo) {
      const mode = isViewVideo ? 'partial' : 'none';
      // eslint-disable-next-line no-console
      console.log(`[CAMERA] Examiner camera (question): ${mode} (key=${this.$store.state.exam.modules[this.$store.state.exam.current.module]?.key})`);
      this.$socket.emit('renderExaminerCamera', mode);
    },
    startReadPhase() {
      clearTimeout(this.readPhaseTimer);
      if (!this.hasReadPhase || this.isIntroLike) {
        this.readPhaseActive = false;
        return;
      }
      this.readPhaseActive = true;
      this.readPhaseTimer = setTimeout(() => {
        this.readPhaseActive = false;
      }, 10000);
    },
  },
  mounted() {
    this.handleRenderExaminerCamera(this.view.video);
    this.startReadPhase();
  },
  beforeUnmount() {
    clearTimeout(this.readPhaseTimer);
  },
  watch: {
    view: {
      handler() {
        this.handleRenderExaminerCamera(this.view.video);
        this.startReadPhase();
      },
      deep: true,
    },
  },
};
</script>
